import React, { useState } from 'react';
import {
  AlertTriangle, CheckCircle, Clock, RefreshCw,
  Activity, HelpCircle, RotateCcw, Check, X
} from 'lucide-react';
import { Button } from '../components/ui/button';

const unknownOutcomes = [
  {
    id: "unk_001",
    caseId: "case_xyz",
    action: "slack.user.invite",
    connector: "slack",
    issue: "Request timeout after 30s",
    lastAttempt: "15m ago",
    retries: 3,
    stateCheck: {
      status: "pending",
      result: null
    }
  },
  {
    id: "unk_002",
    caseId: "case_abc",
    action: "github.repo.create",
    connector: "github",
    issue: "500 error from API",
    lastAttempt: "45m ago",
    retries: 3,
    stateCheck: {
      status: "checked",
      result: "Resource exists - action likely succeeded"
    }
  },
  {
    id: "unk_003",
    caseId: "case_def",
    action: "intune.device.wipe",
    connector: "intune",
    issue: "No response received",
    lastAttempt: "2h ago",
    retries: 2,
    stateCheck: {
      status: "pending",
      result: null
    }
  }
];

const driftItems = [
  {
    id: "drift_001",
    resource: "jsmith@acme.com groups",
    type: "identity",
    expected: ["engineering", "engineering-standard", "slack-users", "github"],
    actual: ["engineering", "engineering-standard", "slack-users", "github", "prod-db-readonly"],
    delta: "+1 group (prod-db-readonly)",
    severity: "medium",
    possibleCauses: ["Manual change in Okta console", "Another system granted access"]
  },
  {
    id: "drift_002",
    resource: "Laptop-123 apps",
    type: "endpoint",
    expected: "12 apps",
    actual: "14 apps",
    delta: "+2 apps (Dropbox, WhatsApp)",
    severity: "high",
    possibleCauses: ["User installed unauthorized apps", "MDM policy not enforced"]
  },
  {
    id: "drift_003",
    resource: "Figma licenses",
    type: "saas",
    expected: "45 seats",
    actual: "48 seats",
    delta: "+3 seats",
    severity: "low",
    possibleCauses: ["Auto-provisioning triggered", "Manual assignment"]
  }
];

const reconciliationMetrics = {
  unknownOutcomes: 3,
  driftDetected: 3,
  resolvedToday: 8,
  avgResolutionTime: "12m"
};

export const Reconciliation = () => {
  const [activeTab, setActiveTab] = useState('unknown');
  const [checkingState, setCheckingState] = useState(null);

  const handleStateCheck = (id) => {
    setCheckingState(id);
    setTimeout(() => setCheckingState(null), 2000);
  };

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-slate-500" />
              Reconciliation Queue
            </h1>
            <p className="text-sm text-slate-500 mt-1">Handle unknown outcomes and drift detection</p>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Unknown Outcomes</span>
              <HelpCircle className="w-4 h-4 text-slate-400" />
            </div>
            <div className="text-2xl font-semibold text-slate-900">{reconciliationMetrics.unknownOutcomes}</div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Drift Detected</span>
              <AlertTriangle className="w-4 h-4 text-slate-400" />
            </div>
            <div className="text-2xl font-semibold text-slate-900">{reconciliationMetrics.driftDetected}</div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Resolved Today</span>
              <CheckCircle className="w-4 h-4 text-primary" />
            </div>
            <div className="text-2xl font-semibold text-slate-900">{reconciliationMetrics.resolvedToday}</div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Avg Resolution</span>
              <Clock className="w-4 h-4 text-primary" />
            </div>
            <div className="text-2xl font-semibold text-slate-900">{reconciliationMetrics.avgResolutionTime}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="border-b border-slate-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('unknown')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'unknown'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Unknown Outcomes ({unknownOutcomes.length})
              </button>
              <button
                onClick={() => setActiveTab('drift')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'drift'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Drift Detected ({driftItems.length})
              </button>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {activeTab === 'unknown' && unknownOutcomes.map((item) => (
              <div key={item.id} className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{item.action}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">
                        {item.connector}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Case: {item.caseId} | Issue: {item.issue}
                    </p>
                    <div className="text-xs text-slate-400 mt-1">
                      Last attempt: {item.lastAttempt} | Retries: {item.retries}/3
                    </div>

                    {item.stateCheck.status === 'checked' && item.stateCheck.result && (
                      <div className="mt-3 p-2 bg-primary/10 rounded-lg text-sm text-primary flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5" />
                        {item.stateCheck.result}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleStateCheck(item.id)}
                        disabled={checkingState === item.id}
                      >
                        {checkingState === item.id ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Checking...
                          </>
                        ) : (
                          'Check State Now'
                        )}
                      </Button>
                      <Button size="sm" variant="outline">
                        <Check className="w-3 h-3" />
                        Mark Successful
                      </Button>
                      <Button size="sm" variant="outline">
                        <RotateCcw className="w-3 h-3" />
                        Retry
                      </Button>
                      <Button size="sm" variant="outline" className="text-slate-900">
                        <X className="w-3 h-3" />
                        Fail Case
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {activeTab === 'drift' && driftItems.map((item) => (
              <div key={item.id} className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    item.severity === 'high' ? 'bg-slate-900' :
                    item.severity === 'medium' ? 'bg-slate-200' : 'bg-primary/10'
                  }`}>
                    <AlertTriangle className={`w-5 h-5 ${
                      item.severity === 'high' ? 'text-white' :
                      item.severity === 'medium' ? 'text-slate-600' : 'text-primary'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{item.resource}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        item.severity === 'high' ? 'bg-slate-900 text-white' :
                        item.severity === 'medium' ? 'bg-slate-200 text-slate-700' :
                        'bg-primary/10 text-primary'
                      }`}>
                        {item.severity}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">
                        {item.type}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg text-sm">
                      <div>
                        <div className="text-xs text-slate-400 mb-1">Expected</div>
                        <div className="text-slate-600">
                          {Array.isArray(item.expected) ? item.expected.join(", ") : item.expected}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-400 mb-1">Actual</div>
                        <div className="text-slate-600">
                          {Array.isArray(item.actual) ? item.actual.join(", ") : item.actual}
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 text-sm text-slate-900 font-medium">
                      Delta: {item.delta}
                    </div>

                    <div className="mt-2 text-xs text-slate-500">
                      Possible causes: {item.possibleCauses.join(" | ")}
                    </div>

                    <div className="flex items-center gap-2 mt-3">
                      <Button size="sm">
                        <Check className="w-3 h-3" />
                        Accept (Update Expected)
                      </Button>
                      <Button size="sm" variant="outline">
                        <RotateCcw className="w-3 h-3" />
                        Revert to Expected
                      </Button>
                      <Button size="sm" variant="outline">
                        <Activity className="w-3 h-3" />
                        Investigate
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
    </div>
  );
};

export default Reconciliation;
